import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { eur } from "@/lib/api";
import { Card, Btn } from "@/components/common";
import { ChevronDown } from "lucide-react";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const ITEMS = [["gst", "GST"], ["service_charge", "Service charge"], ["green_tax", "Green Tax"], ["council_tax", "Council Tax"]];

export function useTaxForecast(year) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get(`/tax-forecast?year=${year}`).then((r) => setD(r.data)).catch(() => setD(null)); }, [year]);
  useEffect(() => { load(); }, [load]);
  return [d, load];
}

// righe da inserire nel Riepilogo spese: MIRA pagato + (dovuto stimato)
export function MiraRows({ mira, tx }) {
  return (
    <>
      <tr className="border-b border-slate-100">
        <td className="py-2 text-slate-800 font-semibold">MIRA</td>
        <td className="py-2 text-right tabular font-semibold" data-testid="annual-mira">
          {eur(mira)} {tx && <span className="text-xs font-normal text-amber-700" data-testid="annual-mira-due">({eur(tx.totals.total)})</span>}
        </td>
      </tr>
      {tx && ITEMS.map(([k, l]) => (
        <tr key={k} className="border-b border-slate-50">
          <td className="py-1.5 pl-4 text-slate-500 text-xs">— {l}</td>
          <td className="py-1.5 text-right tabular text-xs text-amber-700" data-testid={`annual-tax-${k}`}>({eur(tx.totals[k])})</td>
        </tr>
      ))}
    </>
  );
}

function SettingsForm({ tx, onSaved }) {
  const [f, setF] = useState({ ...tx.settings, usd_eur: tx.settings.usd_eur ?? "" });
  const fields = [["per_person_day", "€ a persona al giorno"], ["gst_pct", "GST %"], ["sc_pct", "Service charge %"], ["green_usd", "Green Tax $ / notte"], ["council_usd", "Council Tax $ / notte"], ["usd_eur", "Cambio 1 $ = € (vuoto = automatico)"]];
  const save = async () => {
    try {
      await api.put("/tax-forecast/settings", Object.fromEntries(fields.map(([k]) => [k, f[k] === "" ? null : Number(f[k])])));
      toast.success("Impostazioni tasse salvate");
      onSaved();
    } catch (e) { toast.error("Valori non validi"); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t border-slate-100" data-testid="tax-settings">
      {fields.map(([k, l]) => (
        <label key={k} className="text-[11px] text-slate-500">
          {l}
          <input data-testid={`tax-set-${k}`} type="number" step="0.01" value={f[k] ?? ""} placeholder={k === "usd_eur" ? String(tx.usd_eur) : ""}
            onChange={(e) => setF({ ...f, [k]: e.target.value })} className="block w-28 mt-0.5 px-2 py-1 text-sm text-right rounded border border-slate-300" />
        </label>
      ))}
      <Btn data-testid="tax-settings-save" onClick={save}>Salva</Btn>
    </div>
  );
}

export function TaxForecastCard({ tx, monthly, onSaved }) {
  const [open, setOpen] = useState(false);
  if (!tx) return null;
  const paid = (i) => monthly?.[i]?.mira || 0;
  return (
    <Card className="p-5" data-testid="tax-forecast-card">
      <button data-testid="tax-forecast-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left">
        <h3 className="font-heading font-bold text-slate-900">Tasse previste {tx.year} vs MIRA pagato</h3>
        <span className="text-xs text-slate-400">solo prenotazioni confermate · {tx.totals.person_nights} notti-persona</span>
        <ChevronDown size={16} className={`ml-auto text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-[11px] text-slate-500 mt-1">
        Per persona a notte: GST {eur(tx.per_person.gst)} ({tx.settings.gst_pct}% di {eur(tx.settings.per_person_day)}) · service charge {eur(tx.per_person.service_charge)} ({tx.settings.sc_pct}% senza GST) · Green Tax {tx.settings.green_usd} $ · Council Tax {tx.settings.council_usd} $ (1 $ = {tx.usd_eur} €)
      </p>
      {open && (
        <>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-xs min-w-[640px]" data-testid="tax-forecast-table">
              <thead><tr className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                <th className="text-left py-1.5">Mese</th><th className="text-right">Notti-pers.</th>{ITEMS.map(([k, l]) => <th key={k} className="text-right">{l}</th>)}<th className="text-right">Dovuto</th><th className="text-right">MIRA pagato</th><th className="text-right">Differenza</th>
              </tr></thead>
              <tbody>
                {tx.months.map((m, i) => {
                  const diff = paid(i) - m.total;
                  return (
                    <tr key={m.month} className="border-b border-slate-100" data-testid={`tax-row-${m.month}`}>
                      <td className="py-1.5 font-semibold">{MONTHS[i]}</td>
                      <td className="text-right tabular">{m.person_nights}</td>
                      {ITEMS.map(([k]) => <td key={k} className="text-right tabular">{eur(m[k])}</td>)}
                      <td className="text-right tabular font-semibold text-amber-700">{eur(m.total)}</td>
                      <td className="text-right tabular">{eur(paid(i))}</td>
                      <td className={`text-right tabular ${diff < -1 ? "text-rose-600" : "text-slate-500"}`}>{eur(diff)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <SettingsForm tx={tx} onSaved={onSaved} />
        </>
      )}
    </Card>
  );
}
